import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../Components/Layout";
import { news as data } from "../data/Chinese-Zodiac-Horoscope";
import styles from "../styles/NewsItem.module.css";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../auth/AuthContext";

function normalizeSlug(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/^the-/, "")
    .replace(/\s+/g, "-");
}

/**
 * Keep ONLY the description/introduction content:
 * - Keeps everything BEFORE the first <h3> section header.
 * - Removes Strengths/Challenges/Compatibility/etc for guests + free users.
 */
function keepIntroOnly(html) {
  if (!html) return "";
  const s = String(html);

  // split at first <h3 ...>
  const idx = s.search(/<h3\b/i);
  if (idx === -1) return s; // no sections found, keep all

  return s.slice(0, idx);
}

const ZodiacDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading, isAdmin, hasSubscription } = useAuth();

  const target = normalizeSlug(slug);

  const news =
    data.find(
      (item) => String(item.slug || "").toLowerCase() === String(slug).toLowerCase()
    ) ||
    data.find((item) => normalizeSlug(item.slug) === target) ||
    data.find((item) => normalizeSlug(item.name) === target);

  const canSeeFull = useMemo(() => {
    if (loading) return false;
    if (isAdmin) return true;
    return !!user?.id && !!hasSubscription;
  }, [loading, isAdmin, user?.id, hasSubscription]);

  const structuredData = useMemo(() => {
    if (!news) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://mychinesehoroscope.com/chinese-zodiac/${news.slug}`,
      },
      headline: `Chinese Horoscope Zodiac ${news.name}`,
      image: [news.image],
      datePublished: "2025-08-05",
      dateModified: "2025-08-05",
      author: { "@type": "Organization", name: "My Chinese Horoscope" },
      publisher: {
        "@type": "Organization",
        name: "MyChineseHoroscope.com",
        logo: {
          "@type": "ImageObject",
          url: "https://mychinesehoroscope.com/my-chinese-horoscope-finder.png",
        },
      },
      description: `Explore the characteristics and details about the ${news.name} Chinese Zodiac.`,
      keywords: `${news.name}, Chinese horoscope, zodiac sign, Chinese astrology`,
    };
  }, [news]);

  if (!slug) return <p>Loading...</p>;

  if (!news) {
    return (
      <Layout title="Chinese Horoscope Zodiac Not Found">
        <p>No data found for the zodiac sign: {slug}</p>
        <button onClick={() => navigate("/chinese-zodiac")}>Back to Zodiac List</button>
      </Layout>
    );
  }

  const currentIndex = data.findIndex((item) => item.slug === news.slug);
  const previous = currentIndex > 0 ? data[currentIndex - 1] : null;
  const next =
    currentIndex >= 0 && currentIndex < data.length - 1 ? data[currentIndex + 1] : null;

  // Guests + free users: intro only
  const htmlToRender = canSeeFull ? news.detail : keepIntroOnly(news.detail);

  return (
    <Layout
      title={`Chinese Horoscope Zodiac ${news.name} - My Chinese Horoscope`}
      description={`Explore the characteristics and details about the ${news.name} Chinese Zodiac.`}
      keywords={`${news.name}, Chinese horoscope, zodiac sign, Chinese astrology`}
      imageUrl={news.image}
    >
      {structuredData && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        </Helmet>
      )}

      <div className={styles.container}>
        <h1>Chinese Horoscope {news.name}</h1>

        <div className={styles.newsdate}>Born in: {news.date}</div>

        <div className={styles.newsimg}>
          <img src={news.image} alt={news.name} width={360} height={360} />
        </div>

        <div dangerouslySetInnerHTML={{ __html: htmlToRender }} />

        {/* Optional CTA for non-full users */}
        {!canSeeFull && (
          <div
            style={{
              marginTop: 16,
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 16,
              padding: 18,
              background: "#ff9900",
              color: "#fff",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Learn more about Strengths, Zodiac Compatibility, Challenges & more</h3>
            <p style={{ marginBottom: 12, opacity: 0.9 }}>
              Subscribe to view the full {news.name} profile.
            </p>
        <button
  type="button"
  onClick={() => navigate("/subscribe")}
  style={{
    padding: "12px 18px",
    borderRadius: 12,
    border: "none",
    background: "#fff",
    color: "#ff9900",
    fontWeight: 900,
    cursor: "pointer",
    display: "block",
    margin: "0 auto"   // ✅ centers horizontally
  }}
>
  Subscribe
</button>
          </div>
        )}

        <div className={styles.pagination}>
          {previous && (
            <button onClick={() => navigate(`/chinese-zodiac/${previous.slug}`)}>
              Previous Zodiac: {previous.name}
            </button>
          )}
          <button onClick={() => navigate("/chinese-zodiac")}>Back to Chinese Zodiac List</button>
          {next && (
            <button onClick={() => navigate(`/chinese-zodiac/${next.slug}`)}>
              Next Zodiac: {next.name}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ZodiacDetails;